import { useEffect, useMemo, useState } from 'react';

import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useNetworkPerformanceActions, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';

import SlideoutShell, { BorderedSection } from '../../shared/slideout/SlideoutShell';

import { NetworkPerformanceMetrics } from './constants';
import EntityBreakdownTable, { slugify } from './EntityBreakdownTable';
import EntitySlideoutTiles from './EntitySlideoutTiles';
import NetworkPerformancePatientList from './NetworkPerformancePatientList';
import useEntitySlideoutAnalytics from './useEntitySlideoutAnalytics';

type SlideoutMeta = {
  title: string;
  subtitle: string;
  ready: boolean;
};

export default function EntitySlideout() {
  const slideoutContext = useNetworkPerformanceStore((s) => s.slideoutContext);
  const { closeSlideout } = useNetworkPerformanceActions();
  const startDate = useNetworkPerformanceStore((s) => s.filters[START_DATE]) as Date | undefined;
  const endDate = useNetworkPerformanceStore((s) => s.filters[END_DATE]) as Date | undefined;

  const [meta, setMeta] = useState<SlideoutMeta>({ title: '', subtitle: '', ready: false });

  const entityContext = slideoutContext?.kind === 'entity' ? slideoutContext : null;

  return (
    <SlideoutShell
      open={!!entityContext}
      onClose={closeSlideout}
      title={meta.title}
      subtitle={meta.subtitle}
      subtitleReady={meta.ready}
      startDate={startDate}
      endDate={endDate}>
      {entityContext && (
        <EntitySlideoutContent
          entityId={entityContext.entityId}
          entityName={entityContext.entityName}
          onMeta={setMeta}
        />
      )}
    </SlideoutShell>
  );
}

function EntitySlideoutContent({
  entityId,
  entityName,
  onMeta,
}: {
  entityId: string;
  entityName: string;
  onMeta: (m: SlideoutMeta) => void;
}) {
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);
  const analytics = useEntitySlideoutAnalytics(entityId, entityName);
  const { dischargeRollups, loading } = analytics;

  const entityCount = useMemo(() => {
    const { analyticsResponse } = dischargeRollups;
    const names = new Set(
      analyticsResponse.responseData
        .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
        .map((row) => String(row.dimensionValues[0]?.value ?? ''))
        .filter(Boolean)
    );
    return names.size;
  }, [dischargeRollups]);

  const title = `${entityName} - Overview`;
  const subtitle = `${entityCount} ${selectedGroupType!.displayName}s`;
  const ready = !loading;

  useEffect(() => {
    onMeta({ title, subtitle, ready });
  }, [title, subtitle, ready, onMeta]);

  return (
    <>
      <EntitySlideoutTiles {...analytics} />
      <BorderedSection>
        <EntityBreakdownTable entityId={entityId} entityName={entityName} {...analytics} />
      </BorderedSection>
      {/* OES config uses the same source (LOCATION_EPISODE_DISCHARGES) and group dimension
          (GROUP_NAME) needed for the entity patient list. */}
      <NetworkPerformancePatientList
        metric={NetworkPerformanceMetrics.OES}
        extraFilters={{ providerClient: [{ id: entityId, name: entityName }] }}
        exportFilename={`post-discharge-entity-${slugify(entityName)}-patients`}
      />
    </>
  );
}
